import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bike, CheckCircle2, KeyRound, MapPin, RefreshCw, UserRound } from "lucide-react";
import { partnerApi, formatApiError } from "../lib/api";

const AUTH_KEY = "carza_partner_auth";
const NEXT_ACTIONS = {
  CONFIRMED: [{ action: "on_the_way", label: "Left for customer" }, { action: "in_progress", label: "Started washing" }],
  ASSIGNED: [{ action: "on_the_way", label: "Left for customer" }, { action: "in_progress", label: "Started washing" }],
  PARTNER_ON_THE_WAY: [{ action: "in_progress", label: "Started washing" }],
  SERVICE_IN_PROGRESS: [{ action: "completed", label: "Wash Complete" }],
};

export default function PartnerPage() {
  const [auth, setAuth] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(AUTH_KEY)) || null; } catch { return null; }
  });
  const [me, setMe] = useState(null);
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState("");

  const refresh = async (a = auth) => {
    if (!a) return;
    try {
      const r = await partnerApi.access(a.phone, a.pin);
      setMe(r);
    } catch (e) {
      sessionStorage.removeItem(AUTH_KEY);
      setAuth(null);
      setMe(null);
      toast.error(formatApiError(e, "Could not sign in. Check phone and PIN."));
    }
  };

  useEffect(() => { refresh(auth); }, []);
  useEffect(() => {
    if (!auth) return;
    const t = setInterval(() => refresh(auth), 20000);
    return () => clearInterval(t);
  }, [auth]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await partnerApi.access(phone, pin);
      const a = { phone, pin };
      sessionStorage.setItem(AUTH_KEY, JSON.stringify(a));
      setAuth(a);
      setMe(r);
    } catch (err) {
      toast.error(formatApiError(err, "Wrong phone or PIN."));
    } finally {
      setLoading(false);
    }
  };

  const availability = async (val) => {
    try {
      await partnerApi.setAvailability(auth.phone, auth.pin, val);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const act = async (action) => {
    setActing(action);
    try {
      await partnerApi.jobAction(auth.phone, auth.pin, action);
      await refresh();
      toast.success(action === "completed" ? "Job completed — nice work!" : "Updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing("");
    }
  };

  if (!auth) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-ink px-5">
        <form onSubmit={login} className="glass w-full max-w-sm rounded-3xl p-8" data-testid="partner-login-form">
          <p className="font-logo text-sm tracking-[0.18em] text-paper">CARZA <span className="text-carza">Partner</span></p>
          <p className="mt-2 text-[12px] text-mist">Sign in with your registered phone and PIN.</p>
          <label className="mt-5 flex flex-col gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Phone</span>
            <input className="field" value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="10-digit mobile" inputMode="tel" data-testid="partner-login-phone" />
          </label>
          <label className="mt-3 flex flex-col gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2"><KeyRound size={11} className="mr-1 inline" />PIN</span>
            <input className="field" type="password" value={pin} onChange={(e) => setPin(e.target.value)} required placeholder="Your PIN" inputMode="numeric" data-testid="partner-login-pin" />
          </label>
          <button type="submit" disabled={loading} className="btn btn-primary mt-5 w-full py-3.5 disabled:opacity-50" data-testid="partner-login-submit">
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </main>
    );
  }

  const job = me?.job;
  const actions = job ? (NEXT_ACTIONS[job.status] || []) : [];

  return (
    <main className="min-h-screen bg-ink px-5 pb-24 pt-10">
      <div className="mx-auto flex w-full max-w-md flex-col gap-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-carza-dim text-carza-lite"><UserRound size={20} /></span>
            <div>
              <p className="text-[16px] font-semibold text-paper">{me?.name || "Partner"}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">CARZA Field Partner</p>
            </div>
          </div>
          <button onClick={() => refresh()} className="btn btn-outline px-3 py-2 text-[11px]" data-testid="partner-refresh-btn"><RefreshCw size={13} /></button>
        </div>

        <div className="card-surface flex items-center justify-between rounded-2xl p-4" data-testid="partner-availability-card">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist-2">Availability</p>
            <p className="mt-0.5 text-[15px] font-semibold capitalize text-paper">{me?.availability || "—"}</p>
          </div>
          {me?.availability === "busy" ? (
            <span className="rounded-full border border-carza/40 bg-carza-dim px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-carza-lite">On a job</span>
          ) : me?.availability === "free" ? (
            <button onClick={() => availability("offline")} className="btn btn-outline px-3.5 py-2 text-[12px]" data-testid="partner-go-offline">Go Off Duty</button>
          ) : (
            <button onClick={() => availability("free")} className="btn btn-primary px-3.5 py-2 text-[12px]" data-testid="partner-go-free">I'm Free</button>
          )}
        </div>

        {job ? (
          <div className="card-surface rounded-2xl p-5" data-testid="partner-job-card">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-carza-lite">Current job · {job.booking_id}</p>
            <p className="mt-2 font-display text-xl font-bold text-paper">{job.customer_name}</p>
            <p className="mt-1 flex items-start gap-1.5 text-[13px] leading-relaxed text-mist"><MapPin size={13} className="mt-0.5 shrink-0" /> {job.address}</p>
            <div className="mt-2 grid grid-cols-1 gap-1 text-[13px] text-mist">
              <p><span className="text-mist-2">Plan:</span> <span className="text-paper">{job.plan_name}</span></p>
              <p><span className="text-mist-2">When:</span> <span className="text-paper">{job.date} · {job.time_slot}</span></p>
              {job.vehicle_number && <p><span className="text-mist-2">Car:</span> <span className="text-paper">{job.vehicle_number}</span></p>}
              {job.customer_phone && <p><span className="text-mist-2">Customer:</span> <span className="text-paper">{job.customer_phone}</span></p>}
            </div>
            <p className="mt-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-center font-mono text-[10px] uppercase tracking-widest text-mist-2">
              Status: {job.status.replaceAll("_", " ")}
            </p>
            {actions.length > 0 && (
              <div className="mt-4 flex flex-col gap-2">
                {actions.map((a) => (
                  <button key={a.action} onClick={() => act(a.action)} disabled={!!acting} className={`btn w-full py-3.5 text-[14px] disabled:opacity-50 ${a.action === "completed" ? "btn-primary" : "btn-outline"}`} data-testid={`partner-action-${a.action}`}>
                    {acting === a.action ? "Updating…" : a.action === "completed" ? <><CheckCircle2 size={15} /> {a.label}</> : a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-sm leading-relaxed text-mist-2" data-testid="partner-no-job">
            No job assigned right now.<br />You'll see it here the moment the team assigns you one.
          </div>
        )}
      </div>
    </main>
  );
}
